<template>
    <div class="enhance rounded-content">
        <div class="box">
            <p class="title is-5 mb-0">{{ modalTitle }}</p>

            <p class="mb-4">{{ modalSubtitle }}</p>

            <div v-show="enhancedText" class="field mb-4 content">
                <textarea ref="froalaEl" class="enhance__results" />
            </div>

            <div class="field has-addons enhance__controls" :class="{ 'with-menu': menuIsActive }">
                <e-input
                    v-model="prompt"
                    :loading="loading"
                    :placeholder="modalPlaceholder"
                    class="tw:w-full"
                    :icon="enhancedText ? 'plus-circle' : undefined"
                    :icon-clickable="!loading"
                    icon-right="uil uil-message"
                    :icon-right-clickable="(!loading && prompt)"
                    expanded
                    @keyup.enter="enhance"
                    @icon-click="menuIsActive = !menuIsActive"
                    @icon-right-click="enhance"
                />
            </div>

            <e-menu v-if="menuIsActive" class="enhance__menu">
                <e-menu-list label="Suggested">
                    <e-menu-item label="Revert to Original" @click="revert" />
                </e-menu-list>

                <e-menu-list label="Write">
                    <e-menu-item
                        v-for="(preset, index) in presetPrompts"
                        :key="index"
                        :label="preset.label"
                        @click="applyPreset(preset)"
                    />
                </e-menu-list>
            </e-menu>

            <div v-if="!enhancedText" class="field mb-6">
                <label class="label is-small">Quick Prompts:</label>

                <div class="field is-grouped is-grouped-multiline">
                    <div v-for="(preset, index) in presetPrompts" :key="index" class="control">
                        <e-button
                            size="is-small"
                            type="is-link is-light"
                            class="enhance__preset"
                            :disabled="loading"
                            @click="applyPreset(preset)"
                        >
                            {{ preset.label }}
                        </e-button>
                    </div>
                </div>
            </div>

            <div class="field is-grouped mt-6">
                <p class="control">
                    <button class="button" :disabled="loading" @click="close">Cancel</button>
                </p>

                <p v-if="enhancedText" class="control ml-auto">
                    <button class="button is-info" :disabled="loading || !props.text" @click="accept">
                        Apply Changes
                    </button>
                </p>
            </div>
        </div>
    </div>
</template>

<script setup>
import FroalaEditor from 'froala-editor';
import Button from 'primevue/button';
import InputGroup from 'primevue/inputgroup';
import InputText from 'primevue/inputtext';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import EInput from '@/components/ui/EInput.vue';
import { useLoading } from '@/composables/useLoading.js';
import { useSnackbar } from '@/composables/useSnackbar.js';
import { initialState, setting } from '@/Helpers.js';
import axios from '@/plugins/axios.js';

const props = defineProps({ text: { type: String, required: true } });

const emit = defineEmits(['close', 'accept']);

const snackbar = useSnackbar();
const globalLoading = useLoading();

const prompt = ref('');
const enhancedText = ref('');
const loading = ref(false);
const menuIsActive = ref(false);
const animating = ref(false);
const originalEnhancedText = ref('');
const editorInitialized = ref(false);

const froalaConfig = {
    key: initialState('Froala.key'),
    toolbarInline: false,
    toolbarSticky: false,
    toolbarVisibleWithoutSelection: false,
    quickInsertEnabled: false,
    charCounterCount: false,
    wordCounter: false,
    attribution: false,
    pastePlain: false,
    wordPasteModal: false,
    wordPasteKeepFormatting: false,
    heightMin: 100,
    heightMax: 500,
    placeholderText: '',
    initOnClick: false,
    toolbarButtons: [],
    iframe: false,
};

const froalaEl = ref(null);
const editorInstance = ref(null);

const initEditor = () => {
    if (editorInstance.value || !froalaEl.value) return;

    editorInstance.value = new FroalaEditor(froalaEl.value, {
        ...froalaConfig,
        events: {
            initialized() {
                editorInitialized.value = true;
                this.html.set(enhancedText.value || '');

                const editor = this;
                nextTick(() => {
                    if (editor.el && editor.o_wrapperEl && (enhancedText.value || editor.html.get())) {
                        const editorContentElement = editor.el;
                        const wrapperElement = editor.o_wrapperEl;
                        const minH = editor.opts.heightMin || 80;
                        const maxH = editor.opts.heightMax || 500;

                        let initialHeight = editorContentElement.scrollHeight;
                        initialHeight = Math.max(minH, initialHeight);
                        initialHeight = Math.min(maxH, initialHeight);
                        wrapperElement.style.height = `${initialHeight}px`;
                    }
                });
            },
            contentChanged() {
                if (editorInstance.value && this.html.get() !== enhancedText.value) {
                    enhancedText.value = this.html.get();
                }
            },
        },
    });
};

onMounted(async () => {
    await nextTick();
    if (froalaEl.value && !editorInstance.value) {
        initEditor();
    }
});

let activeLoader = null;

onBeforeUnmount(() => {
    if (activeLoader) {
        activeLoader.close();
        activeLoader = null;
    }

    if (editorInstance.value) {
        editorInstance.value.destroy();
        editorInstance.value = null;
    }
});

watch(enhancedText, newValue => {
    if (editorInstance.value && editorInitialized.value && editorInstance.value.html) {
        if (editorInstance.value.html.get() !== newValue) {
            editorInstance.value.html.set(newValue);
        }
    }
});

watch(loading, isValuesLoading => {
    if (isValuesLoading) {
        activeLoader = globalLoading.open({ message: 'Enhancing text...' });
    } else if (activeLoader) {
        activeLoader.close();
        activeLoader = null;
    }
});

watch(animating, isAnimating => {
    if (editorInstance.value && editorInstance.value.o_wrapperEl) {
        const wrapper = editorInstance.value.o_wrapperEl;
        if (isAnimating) {
            wrapper.classList.add('froala-wrapper-animating-height');
        } else {
            wrapper.classList.remove('froala-wrapper-animating-height');
        }
    }
});

const modalTitle = computed(() => (enhancedText.value ? 'Review your content.' : 'How can I assist you?'));

const modalSubtitle = computed(() =>
    enhancedText.value ? 'Select what you want to do next' : 'I can enhance your text, correct grammar and much more.'
);

const modalPlaceholder = computed(() =>
    enhancedText.value
        ? 'Click the + icon to see more, or write your prompt.'
        : 'Write your own prompt to edit or write.'
);

const presetPrompts = ref([
    {
        label: 'Simplify Writing',
        prompt: 'Simplify this text for better student comprehension, using clearer language and shorter sentences.',
    },
    {
        label: 'Make Engaging',
        prompt: 'Make this text more engaging and motivating for students by adding relevant examples and enthusiasm.',
    },
    {
        label: 'Add Instructions',
        prompt: 'Add clear, numbered instructions for students to follow in this announcement.',
    },
    {
        label: 'Friendlier Tone',
        prompt: 'Adjust the tone to be warmer and more approachable for student communications.',
    },
    {
        label: 'Highlight Deadlines',
        prompt: 'Emphasize important dates and deadlines in this text using formatting and clear language.',
    },
    {
        label: 'Clarify Steps',
        prompt: 'Break down any complex processes into clearer step-by-step instructions.',
    },
    {
        label: 'Add Encouragement',
        prompt: 'Add encouraging language to motivate students and acknowledge potential challenges.',
    },
    {
        label: 'Add Examples',
        prompt: 'Add relevant educational examples to illustrate key concepts and improve understanding.',
    },
    {
        label: 'Add Reminders',
        prompt: 'Add helpful reminders about resources, office hours, or support services available to students.',
    },
]);

const animateAndSet = async targetText => {
    if (loading.value) return;

    if (!editorInstance.value || !editorInitialized.value || !editorInstance.value.html) {
        enhancedText.value = targetText;
        animating.value = false;
        return;
    }

    animating.value = true;
    await nextTick();

    originalEnhancedText.value = targetText;

    const currentEditorOpts = editorInstance.value.opts;
    const animMinHeight = currentEditorOpts.heightMin || 100;

    if (editorInstance.value && editorInstance.value.o_wrapperEl) {
        const wrapper = editorInstance.value.o_wrapperEl;
        wrapper.classList.remove('froala-wrapper-animating-height');
        wrapper.style.height = '';
        // eslint-disable-next-line no-unused-expressions
        wrapper.offsetHeight;
        wrapper.classList.add('froala-wrapper-animating-height');
    }
    await nextTick();

    const updateEditorPresentation = async () => {
        await nextTick();

        if (editorInstance.value && editorInstance.value.el && editorInstance.value.o_wrapperEl) {
            const editorContentElement = editorInstance.value.el;
            const wrapperElement = editorInstance.value.o_wrapperEl;

            let newHeight = editorContentElement.scrollHeight;

            let effectiveMaxHeight = currentEditorOpts.heightMax || 400;
            if (currentEditorOpts.heightMax && currentEditorOpts.heightMax > 0) {
                effectiveMaxHeight = Math.min(effectiveMaxHeight, currentEditorOpts.heightMax);
            }

            newHeight = Math.max(animMinHeight, newHeight);
            const wrapperMinHeight = parseFloat(getComputedStyle(wrapperElement).minHeight);
            if (!Number.isNaN(wrapperMinHeight)) {
                newHeight = Math.max(newHeight, wrapperMinHeight);
            }
            newHeight = Math.min(effectiveMaxHeight, newHeight);

            wrapperElement.style.height = `${newHeight}px`;
        }
    };

    enhancedText.value = '';
    await nextTick();

    await updateEditorPresentation();

    if (!targetText || !targetText.trim()) {
        setTimeout(() => {
            animating.value = false;
        }, 350);
        return;
    }

    const duration = 500;
    const startTime = performance.now();

    const animateFrame = async timestamp => {
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const charCount = Math.floor(targetText.length * progress);
        const currentAnimatedText = targetText.substring(0, charCount);

        if (enhancedText.value !== currentAnimatedText) {
            enhancedText.value = currentAnimatedText;
        }

        await nextTick();
        await updateEditorPresentation();

        if (progress < 1) {
            requestAnimationFrame(animateFrame);
        } else {
            if (enhancedText.value !== targetText) {
                enhancedText.value = targetText;
            }
            await nextTick();
            await updateEditorPresentation();
            animating.value = false;
        }
    };

    requestAnimationFrame(animateFrame);

    setTimeout(() => {
        animating.value = false;
    }, 350);
};

const error = message => {
    snackbar.open({ message, type: 'is-danger', position: 'is-bottom-left', actionText: 'Dismiss', duration: 5000 });
};

const enhance = async () => {
    const payload = enhancedText.value || props.text;
    const maxLength = setting('enhancement_text_max_length');

    if (payload.length > maxLength) {
        error(`The text to enhance cannot exceed ${maxLength} characters.`);

        return;
    }

    loading.value = true;

    try {
        const response = await axios.post('/api/enhance', { type: 'text', prompt: prompt.value || null, payload });

        loading.value = false;

        if (response.data.status === 'success') {
            prompt.value = '';

            await animateAndSet(response.data.enhanced);
        } else {
            error(response.data.message || 'Failed to enhance text. Please try again.');
        }
    } catch (err) {
        loading.value = false;

        error('An error occurred while enhancing.');
    }
};

const applyPreset = preset => {
    prompt.value = preset.prompt;

    menuIsActive.value = false;
};

const revert = () => {
    enhancedText.value = props.text;

    menuIsActive.value = false;
};

const close = () => emit('close');

const accept = () => {
    emit('accept', enhancedText.value);

    close();
};
</script>

<style lang="scss">
@import '~assets/scss/toolbox.scss';

.enhance {
    border-radius: $radius-large;
    overflow: visible;
    margin: 0;

    /* Fallback textarea (before Froala loads) */
    &__results {
        border: 1px solid $success;
        font-weight: 300;
        border-radius: $radius-large;
        font-family: $family-sans-serif;
    }

    /* Generated Froala container */
    .fr-box.fr-basic {
        border: 1px solid $success !important;
        border-radius: $radius-large !important;
        background: transparent;
        overflow: visible;

        &:hover,
        &:focus-within {
            border-color: $success !important;
        }

        &:focus-within {
            box-shadow: none;
        }

        .fr-element {
            background: transparent;
            border: none;
            font-family: $family-sans-serif;
            overflow-y: auto !important;
        }

        .fr-wrapper {
            background: var(--p-content-background);
            border: none;
            border-radius: $radius-large;
            box-shadow: none;
            resize: vertical;
            overflow: auto;
            transition-property: height;
            transition-duration: 0s; // No animation for manual resize
            transition-timing-function: ease;

            &.froala-wrapper-animating-height {
                transition-duration: 0.3s; // Animate height when 'animating' is true
            }
        }
    }

    .fr-toolbar,
    .fr-counter,
    .fr-second-toolbar,
    .fr-quick-insert,
    .fr-statusbar,
    a.fr-logo {
        display: none !important;
    }

    .fr-wrapper,
    .fr-element {
        min-height: 12rem;
    }

    &__controls {
        width: 100%;

        .p-inputgroup {
            width: 100%;
        }

        .enhance__input {
            flex: 1 1 auto;
            min-width: 0;
        }

        &.with-menu {
            .p-inputgroup .p-button:first-child {
                border-bottom-left-radius: 0;
            }

            .p-inputgroup .p-button:last-child {
                border-bottom-right-radius: 0;
            }
        }
    }

    &__preset {
        padding: 0.2rem 0.8rem;
        height: auto;
    }

    &__menu {
        background-color: white;
        border: 1px solid $border;
        border-top: none;
        border-radius: 0 0 $radius-large $radius-large;
        box-shadow: 0px 8px 16px 2px rgba(36, 43, 54, 0.1);
        margin-top: -1.425rem !important;
        max-height: 12rem;
        overflow-y: auto;
        padding: $size-7;

        .menu-list a {
            padding: 0.56rem $size-7;
            border-radius: $radius;

            &:hover {
                background-color: $primary;
                color: $white;
            }
        }
    }

    &__icon {
        i {
            background: linear-gradient(243deg, #00e8fd 23.35%, #0000c9 46.16%, #9400c9 69.28%);
            background-clip: text;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            color: transparent;
        }
    }
}

.is-transparent-background-modal {
    .modal-background {
        @include from($tablet) {
            background-color: transparent !important;
        }
    }

    .modal-content {
        @include from($tablet) {
            box-shadow:
                0px 8px 16px 2px rgba(36, 43, 54, 0.1),
                0px 0px 0px 1px rgba(36, 43, 54, 0.02);
            min-width: 600px;
            max-width: 600px;
            width: 600px;
        }
    }
}
</style>

<style lang="scss" scoped>

:deep(.p-inputgroupaddon) {
    background-color: var(--primary-color);
    color: white;
}
</style>
